import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, UserCheck, AlertCircle, CheckCircle, Settings, Shield, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getRoleDisplayName, getRoleColor } from '../utils/roleMapping';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TeamAdmin() {
  const [allUsers, setAllUsers] = useState([]);
  const [teamStructure, setTeamStructure] = useState({});
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState({ role: '', leader_id: '' });
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [usersResponse, structureResponse, permissionsResponse] = await Promise.all([
        axios.get(`${API}/admin/users/all`, { headers }),
        axios.get(`${API}/admin/team-structure`, { headers }),
        axios.get(`${API}/admin/permissions`, { headers })
      ]);

      setAllUsers(usersResponse.data);
      setTeamStructure(structureResponse.data);
      setPermissions(permissionsResponse.data);
      setLoading(false);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to fetch team data');
      setLoading(false);
    }
  };

  const handleAssignTeam = async (userId) => {
    if (!assignmentForm.role) {
      alert('Please select a role');
      return;
    }

    if (assignmentForm.role !== 'satellite' && !assignmentForm.leader_id) {
      alert('Please select a leader');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/users/${userId}/assign-team`,
        {
          role: assignmentForm.role,
          leader_id: assignmentForm.role === 'satellite' ? null : assignmentForm.leader_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Team assignment successful!');
      setSelectedUser(null);
      setAssignmentForm({ role: '', leader_id: '' });
      fetchData(); // Refresh data
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign team');
    }
    setSaving(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'satellite': return 'bg-purple-100 text-purple-800';
      case 'fsl': return 'bg-blue-100 text-blue-800';
      case 'fta': return 'bg-green-100 text-green-800';
      case 'agent': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAvailableRoles = () => {
    const roles = [];
    if (permissions.can_create_agent) roles.push({ value: 'agent', label: 'Agent' });
    if (permissions.can_create_fta) roles.push({ value: 'fta', label: 'Field Training Agent (FTA)' });
    if (permissions.can_create_fsl) roles.push({ value: 'fsl', label: 'Field Sales Leader (FSL)' });
    if (permissions.can_create_satellite) roles.push({ value: 'satellite', label: 'Satellite (Top Level)' });
    return roles;
  };

  const getAvailableLeaders = (role) => {
    if (role === 'satellite') return [];
    
    // Filter leaders based on hierarchy and user permissions
    if (role === 'fsl') {
      return teamStructure.satellites || [];
    } else if (role === 'fta') {
      return teamStructure.fsls || [];
    } else if (role === 'agent') {
      // For FTAs: only show themselves as leader option
      if (permissions.role === 'fta') {
        return allUsers.filter(u => u.id === user?.id) || [];
      }
      // For FSLs and Satellites: show all FTAs
      return teamStructure.ftas || [];
    }
    return [];
  };

  const getAdminScopeText = () => {
    switch (permissions.admin_scope) {
      case 'satellite': return 'Full Organization';
      case 'fsl': return 'Your FSL Team';
      case 'fta': return 'Your Agent Team';
      default: return 'Limited Access';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
              <Settings className="w-8 h-8 mr-3" />
              Team Administration
            </h1>
            <p className="text-gray-600 dark:text-gray-300 dark:text-gray-300">Manage team assignments and hierarchy structure</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-400">Admin Scope</div>
              <div className="font-semibold text-gray-900 dark:text-white flex items-center">
                <Shield className="w-4 h-4 mr-1" />
                {getAdminScopeText()}
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(permissions.role)}`}>
              {permissions.role?.toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Team Structure Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Current Team Structure</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Object.entries(teamStructure).map(([roleGroup, users]) => (
            <div key={roleGroup} className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 capitalize">{roleGroup}</h3>
              <div className="space-y-2">
                {users?.map(user => (
                  <div key={user.id} className="text-sm">
                    <div className="font-medium">{user.name}</div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">{user.email}</div>
                  </div>
                ))}
                {(!users || users.length === 0) && (
                  <div className="text-gray-400 text-sm">No users</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* All Users List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">All Users</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Current Role</th>
                <th className="text-left p-3">Leader</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allUsers.map(user => {
                const leader = allUsers.find(u => u.id === user.leader_id);
                return (
                  <tr key={user.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="p-3 font-medium">{user.name}</td>
                    <td className="p-3 text-gray-600 dark:text-gray-300 dark:text-gray-300">{user.email}</td>
                    <td className="p-3">
                      {user.role ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          {getRoleDisplayName(user.role)}
                        </span>
                      ) : (
                        <span className="text-gray-400">No role</span>
                      )}
                    </td>
                    <td className="p-3 text-gray-600 dark:text-gray-300 dark:text-gray-300">
                      {leader ? leader.name : user.role === 'satellite' ? 'None (Top Level)' : 'Not assigned'}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Assign Team
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assignment Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Assign Team for {selectedUser.name}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={assignmentForm.role}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, role: e.target.value, leader_id: '' })}
                  className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2"
                >
                  <option value="">Select role...</option>
                  <option value="satellite">Satellite (Top Level)</option>
                  <option value="fsl">Field Sales Leader (FSL)</option>
                  <option value="fta">Field Training Agent (FTA)</option>
                  <option value="agent">Agent</option>
                </select>
              </div>

              {assignmentForm.role && assignmentForm.role !== 'satellite' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Leader
                  </label>
                  <select
                    value={assignmentForm.leader_id}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, leader_id: e.target.value })}
                    className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2"
                  >
                    <option value="">Select leader...</option>
                    {getAvailableLeaders(assignmentForm.role).map(leader => (
                      <option key={leader.id} value={leader.id}>
                        {leader.name} ({leader.role?.toUpperCase()})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setAssignmentForm({ role: '', leader_id: '' });
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAssignTeam(selectedUser.id)}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Assign Team
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamAdmin;